import {
  AdvanceBookingEntityRequest,
  AdvanceBookingEntityResponse,
  CapacityUtilizationEntityRequest,
  CapacityUtilizationEntityResponse,
  CompetitorsBenchmarksEntityRequest,
  CompetitorsBenchmarksEntityResponse,
  CronsExpression,
  FormAlertModel,
  PricingErrorsEntityRequest,
  PricingErrorsEntityResponse,
  RpdEntityRequest,
  RpdEntityResponse,
  UtilizationAvailabilityEntityRequest,
  UtilizationAvailabilityEntityResponse
} from "../../../api/types/alert";
import { alertsAtom, initialFormAlertModel, schedulingAtom, unitAtom } from "../../../state/alertRecoil";
import { SelectElementModelBE, SelectElementModelFE } from "../../../api/types/generic";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import { useRecoilState } from "recoil";
import { UseFormReturn } from "react-hook-form";
import { fromGenericBeToGenericFe } from "../../../utils";
import { useContext } from "react";
import { GlobalContext } from "../../../../../util/state/GlobalContextProvider";

export const useAlertFormUtils = (form: UseFormReturn<FormAlertModel, any>) => {
  // Use Translation
  const { t } = useTranslation(["translation", "pricing"]);

  // Use Context
  const { dispatchGlobalData, globalData: { user } } = useContext(GlobalContext);

  //setto tutte le legalEntities options e le passo al filtro di ricerca ["all", "drivalia_it","drivalia_fr"]
  const legalEntityOptions: SelectElementModelFE[] = user.legalEntities?.map(
    (el: SelectElementModelBE) => ({
      code: el.name,
      label: t("translation:legalEntities.".concat(el.name))
    })
  );

  //se utente clicca su all, mando  ["drivalia_it","drivalia_fr"]
  const optionsFiltered = legalEntityOptions
    .filter(function(el) {
      return el.code !== "all";
    });

  const everyLegalEntitiesWithoutAll = legalEntityOptions
    .map((el) => el.code as string)
    .filter((el) => el !== "all");

  //setto tutti i dealers options
  const dealerOption: SelectElementModelFE[] = user.dealers?.map(
    (el: SelectElementModelBE) => ({
      code: el.name,
      label: t("translation:dealers.".concat(el.name))
    })
  );

  const dealerFilteredOptions = dealerOption
    .map((el) => el.code as string)
    .filter((el) => el !== "all");

  // Use Recoil
  const [alertsList] = useRecoilState(alertsAtom);
  const [schedulingList] = useRecoilState(schedulingAtom);
  const [unitList] = useRecoilState(unitAtom);

  const revertCronExpressions = (
    alert:
      | CapacityUtilizationEntityResponse
      | UtilizationAvailabilityEntityResponse
      | RpdEntityResponse
      | PricingErrorsEntityResponse
      | AdvanceBookingEntityResponse
  ): CronsExpression => {
    const values: string[] = alert.crons;
    let addTime: null | Date = null;
    const firstCronValuesArray = values[0].split(" ");

    // ORARIO - START //
    const startTime: Date = dayjs()
      .minute(Number(firstCronValuesArray[1]))
      .hour(Number(firstCronValuesArray[2]))
      .toDate();

    if (values[1]) {
      const secondCronValuesArray = values[1].split(" ");
      addTime = dayjs()
        .minute(Number(secondCronValuesArray[1]))
        .hour(Number(secondCronValuesArray[2]))
        .toDate();
    }
    // ORARIO - END //

    // RADIO TYPE - START //
    let currentRadioType: string = "";
    if (alert.schedulingType === "DAILY") {
      currentRadioType = t("pricing:alert.labels.everyWeekDay");
    } else if (alert.schedulingType === "WEEKLY") {
      currentRadioType = t("pricing:alert.labels.every");
    } else if (alert.schedulingType === "MONTHLY") {
      if (firstCronValuesArray[3] === "L") {
        currentRadioType = t("pricing:alert.labels.lastDayOfTheMonth");
      } else {
        currentRadioType = t("pricing:alert.labels.every");
      }
    }
    // RADIO TYPE - END //

    // GIORNI DELLA SETTIMANA - START //
    const weekDaysArray = firstCronValuesArray[5].split(",");
    const currentWeeklyDays: SelectElementModelFE[] = [];

    weekDaysArray.forEach((weekDay) => {
      if (
        weekDay ===
        dayjs().locale("en").day(0).format("ddd").toUpperCase()
      ) {
        currentWeeklyDays.push({
          code: 0,
          label: dayjs().day(0).format("dddd")
        });
      } else if (
        weekDay ===
        dayjs().locale("en").day(1).format("ddd").toUpperCase()
      ) {
        currentWeeklyDays.push({
          code: 1,
          label: dayjs().day(1).format("dddd")
        });
      } else if (
        weekDay ===
        dayjs().locale("en").day(2).format("ddd").toUpperCase()
      ) {
        currentWeeklyDays.push({
          code: 2,
          label: dayjs().day(2).format("dddd")
        });
      } else if (
        weekDay ===
        dayjs().locale("en").day(3).format("ddd").toUpperCase()
      ) {
        currentWeeklyDays.push({
          code: 3,
          label: dayjs().day(3).format("dddd")
        });
      } else if (
        weekDay ===
        dayjs().locale("en").day(4).format("ddd").toUpperCase()
      ) {
        currentWeeklyDays.push({
          code: 4,
          label: dayjs().day(4).format("dddd")
        });
      } else if (
        weekDay ===
        dayjs().locale("en").day(5).format("ddd").toUpperCase()
      ) {
        currentWeeklyDays.push({
          code: 5,
          label: dayjs().day(5).format("dddd")
        });
      } else if (
        weekDay ===
        dayjs().locale("en").day(6).format("ddd").toUpperCase()
      ) {
        currentWeeklyDays.push({
          code: 6,
          label: dayjs().day(6).format("dddd")
        });
      }
    });
    // GIORNI DELLA SETTIMANA - END //

    // GIORNI DEL MESE - START //
    let currentMonthlyDays: string = "";
    if (alert.schedulingType === "MONTHLY") {
      if (firstCronValuesArray[3] === "L") {
        currentMonthlyDays = "";
      } else {
        currentMonthlyDays = firstCronValuesArray[3];
      }
    }
    // GIORNI DEL MESE - END //

    return {
      radioType: currentRadioType,
      startTime,
      addTime,
      weeklyDays: currentWeeklyDays,
      monthlyDays: currentMonthlyDays
    };
  };

  const buildFormFromEntity = (
    alert:
      | CapacityUtilizationEntityResponse
      | UtilizationAvailabilityEntityResponse
      | CompetitorsBenchmarksEntityResponse
      | RpdEntityResponse
      | PricingErrorsEntityResponse
      | AdvanceBookingEntityResponse
    , commercialSegmentList: SelectElementModelFE[], t: any) => {
    const formValues: FormAlertModel = JSON.parse(JSON.stringify(initialFormAlertModel(t, legalEntityOptions, dealerOption)));
    formValues.uuid = alert.uuid;
    formValues.type = alertsList.find(
      (a) => a.code === alert.type
    ) as SelectElementModelFE;
    formValues.description = alert.description;
    formValues.recipientLists = alert.recipientLists;
    if (alert.dealers) {
      formValues.dealer = {
        code: alert.dealers[0] as string,
        label: t("translation:dealers.".concat(alert.dealers as string))
      };
    } else if (alert.dealer) {
      formValues.dealer = {
        code: alert.dealer[0] as string,
        label: t("translation:dealers.".concat(alert.dealer as string))
      };
    }

    if (alert.legalEntities) {
      formValues.legalEntity = {
        code: alert.legalEntities[0] as string,
        label: t("translation:legalEntities.".concat(alert.legalEntities as string))
      };
    } else if (alert.legalEntity) {
      formValues.legalEntity = {
        code: alert.legalEntity[0] as string,
        label: t("translation:legalEntities.".concat(alert.legalEntity as string))
      };
    }
    if (alert.type === "CAPACITY_UTILIZATION") {
      formValues.capacityOfLocations.remainingCapacityDays = (
        alert as CapacityUtilizationEntityResponse
      ).remainingCapacityDays as number;
      formValues.capacityOfLocations.remainingPercentage =
        ((alert as CapacityUtilizationEntityResponse)
          .remainingPercentage as number);
      formValues.capacityOfLocations.remainingCapacity = (
        alert as CapacityUtilizationEntityResponse
      ).remainingCapacity as number;
      formValues.capacityOfLocations.usagePercentage =
        ((alert as CapacityUtilizationEntityResponse)
          .usagePercentage as number);
      formValues.schedulingType = schedulingList.find(
        (s) =>
          s.code ===
          (alert as CapacityUtilizationEntityResponse).schedulingType
      ) as SelectElementModelFE;
      formValues.locations = fromGenericBeToGenericFe((alert as CapacityUtilizationEntityResponse)
        .locations as SelectElementModelBE[]) as SelectElementModelFE[];
      formValues.locationGroups = fromGenericBeToGenericFe((alert as CapacityUtilizationEntityResponse)
        .locationGroups as SelectElementModelBE[]) as SelectElementModelFE[];
      formValues.capacityOfLocations.firstRule =
        t(
          "pricing:alert.labels.rules.capacityOfLocationsRules.firstPartRule1"
        ) +
        (alert as CapacityUtilizationEntityResponse)
          .remainingPercentage;
      formValues.capacityOfLocations.secondRule =
        t(
          "pricing:alert.labels.rules.capacityOfLocationsRules.firstPartRule2"
        ) +
        (alert as CapacityUtilizationEntityResponse).remainingCapacity +
        t(
          "pricing:alert.labels.rules.capacityOfLocationsRules.secondPartRule2"
        ) +
        (alert as CapacityUtilizationEntityResponse).usagePercentage;
      formValues.crons = revertCronExpressions(
        alert as CapacityUtilizationEntityResponse
      );
    } else if (alert.type === "UTILIZATION_AVAILABILITY") {
      formValues.schedulingType = schedulingList.find(
        (s) =>
          s.code ===
          (alert as UtilizationAvailabilityEntityResponse)
            .schedulingType
      ) as SelectElementModelFE;
      formValues.locations = fromGenericBeToGenericFe((alert as UtilizationAvailabilityEntityResponse)
        .locations as SelectElementModelBE[]) as SelectElementModelFE[];
      formValues.locationGroups = fromGenericBeToGenericFe((alert as UtilizationAvailabilityEntityResponse)
        .locationGroups as SelectElementModelBE[]) as SelectElementModelFE[];
      formValues.availabilityOfVehicles.maxDailyBookings = (
        alert as UtilizationAvailabilityEntityResponse
      ).maxDailyBookings as number;
      formValues.availabilityOfVehicles.securityMargin = (
        alert as UtilizationAvailabilityEntityResponse
      ).securityMargin as number;
      formValues.availabilityOfVehicles.firstRule =
        t(
          "pricing:alert.labels.rules.availabilityOfVehicles.firstPartRule1"
        ) +
        ((alert as UtilizationAvailabilityEntityResponse)
            .maxDailyBookings -
          (alert as UtilizationAvailabilityEntityResponse)
            .securityMargin);
      formValues.crons = revertCronExpressions(
        alert as UtilizationAvailabilityEntityResponse
      );
      formValues.availabilityOfVehicles.commercialSegment = commercialSegmentList.find((el: SelectElementModelFE) => el.code === (alert as UtilizationAvailabilityEntityResponse)?.commercialSegment) as SelectElementModelFE;
    } else if (alert.type === "COMPETITORS_BENCHMARK") {
      formValues.competitorsBenchmarks.topPricePosition = (
        alert as CompetitorsBenchmarksEntityResponse
      ).topPricePosition as number;
      formValues.competitorsBenchmarks.bottomPricePosition = (
        alert as CompetitorsBenchmarksEntityResponse
      ).bottomPricePosition as number;
      formValues.competitorsBenchmarks.firstRule =
        t(
          "pricing:alert.labels.rules.competitorsBenchmarks.firstPartRules"
        ) +
        (alert as CompetitorsBenchmarksEntityResponse).topPricePosition;
      formValues.competitorsBenchmarks.secondRule =
        t(
          "pricing:alert.labels.rules.competitorsBenchmarks.firstPartRules"
        ) +
        (alert as CompetitorsBenchmarksEntityResponse)
          .bottomPricePosition;
    } else if (alert.type === "RPD_PICKUP_DATE_LOCATION") {
      formValues.schedulingType = schedulingList.find(
        (s) => s.code === (alert as RpdEntityResponse).schedulingType
      ) as SelectElementModelFE;
      formValues.locations = fromGenericBeToGenericFe((alert as RpdEntityResponse)
        .locations as SelectElementModelBE[]) as SelectElementModelFE[];
      formValues.locationGroups = fromGenericBeToGenericFe((alert as RpdEntityResponse)
        .locationGroups as SelectElementModelBE[]) as SelectElementModelFE[];
      formValues.rpd.pickupDateFrom = (alert as RpdEntityResponse)
        .pickupDateFrom as string;
      formValues.rpd.pickupDateTo = (alert as RpdEntityResponse)
        .pickupDateTo as string;
      formValues.rpd.minRPD = (alert as RpdEntityResponse)
        .minRPD / 100 as number;
      formValues.rpd.firstRule =
        t("pricing:alert.labels.rules.rpd.firstPartRule1") +
        (alert as RpdEntityResponse).minRPD / 100;
      formValues.crons = revertCronExpressions(
        alert as RpdEntityResponse
      );
      formValues.rpd.commercialSegment = commercialSegmentList.find((el: SelectElementModelFE) => el.code === (alert as RpdEntityResponse)?.commercialSegment) as SelectElementModelFE;
    } else if (alert.type === "PRICING_ERRORS") {
      formValues.schedulingType = schedulingList.find(
        (s) =>
          s.code ===
          (alert as PricingErrorsEntityResponse).schedulingType
      ) as SelectElementModelFE;
      formValues.locations = fromGenericBeToGenericFe((alert as PricingErrorsEntityResponse)
        .locations as SelectElementModelBE[]) as SelectElementModelFE[];
      formValues.locationGroups = fromGenericBeToGenericFe((alert as PricingErrorsEntityResponse)
        .locationGroups as SelectElementModelBE[]) as SelectElementModelFE[];
      formValues.pricingErrors.pickupDateFrom = (
        alert as PricingErrorsEntityResponse
      ).pickupDateFrom as string;
      formValues.pricingErrors.pickupDateTo = (
        alert as PricingErrorsEntityResponse
      ).pickupDateTo as string;
      formValues.pricingErrors.unitType = unitList.find(
        (u) =>
          u.code === (alert as PricingErrorsEntityResponse).unitType
      ) as SelectElementModelFE;
      formValues.pricingErrors.minDailyPrice = (
        alert as PricingErrorsEntityResponse
      ).minDailyPrice / 100 as number;
      formValues.pricingErrors.firstRule =
        t("pricing:alert.labels.rules.pricingErrors.firstPartRule1") +
        (alert as PricingErrorsEntityResponse).minDailyPrice / 100;
      formValues.crons = revertCronExpressions(
        alert as PricingErrorsEntityResponse
      );
      formValues.pricingErrors.commercialSegment = commercialSegmentList.find((el: SelectElementModelFE) => el.code === (alert as PricingErrorsEntityResponse)?.commercialSegment) as SelectElementModelFE;
    } else if (alert.type === "ADVANCE_BOOKING") {
      formValues.schedulingType = schedulingList.find(
        (s) =>
          s.code ===
          (alert as AdvanceBookingEntityResponse).schedulingType
      ) as SelectElementModelFE;
      formValues.locations = fromGenericBeToGenericFe((alert as AdvanceBookingEntityResponse)
        .locations as SelectElementModelBE[]) as SelectElementModelFE[];
      formValues.locationGroups = fromGenericBeToGenericFe((alert as AdvanceBookingEntityResponse)
        .locationGroups as SelectElementModelBE[]) as SelectElementModelFE[];
      formValues.advanceBooking.pickupDateFrom = (
        alert as AdvanceBookingEntityResponse
      ).pickupDateFrom as string;
      formValues.advanceBooking.pickupDateTo = (
        alert as AdvanceBookingEntityResponse
      ).pickupDateTo as string;
      formValues.advanceBooking.pastDays = (
        alert as AdvanceBookingEntityResponse
      ).pastDays as number;
      formValues.advanceBooking.pastOrFutureDays = (
        alert as AdvanceBookingEntityResponse
      ).pastOrFutureDays as number;
      formValues.advanceBooking.averageBookingsNumber = (
        alert as AdvanceBookingEntityResponse
      ).averageBookingsNumber as number;
      formValues.crons = revertCronExpressions(
        alert as AdvanceBookingEntityResponse
      );
      formValues.advanceBooking.commercialSegment = commercialSegmentList.find((el: SelectElementModelFE) => el.code === (alert as AdvanceBookingEntityResponse)?.commercialSegment) as SelectElementModelFE;
    }
    return formValues;
  };

  const buildCronExpressions = (values: FormAlertModel): string[] => {
    let startTimeCronExpression: string = "0 * * * * * *";
    let addTimeCronExpression: string = "0 * * * * * *";

    const cronExpressions: string[] = [];

    const startTimeMinute = dayjs(values.crons.startTime).minute();
    const startTimeHour = dayjs(values.crons.startTime).hour();
    const addTimeMinute = dayjs(values.crons.addTime).minute();
    const addTimeHour = dayjs(values.crons.addTime).hour();

    if (values.schedulingType?.code === "DAILY") {
      startTimeCronExpression = `0 ${startTimeMinute} ${startTimeHour} ? * * *`;
      addTimeCronExpression = `0 ${addTimeMinute} ${addTimeHour} ? * * *`;
    } else if (values.schedulingType?.code === "WEEKLY") {
      const weekDay = values.crons.weeklyDays
        .map((d) =>
          dayjs()
            .day(d.code as number)
            .locale("en")
            .format("ddd")
            .toUpperCase()
        )
        .join(",");

      startTimeCronExpression = `0 ${startTimeMinute} ${startTimeHour} ? * ${weekDay} *`;
      addTimeCronExpression = `0 ${addTimeMinute} ${addTimeHour} ? * ${weekDay} *`;
    } else if (values.schedulingType?.code === "MONTHLY") {
      const monthDay =
        values.crons.radioType ===
        t("pricing:alert.labels.lastDayOfTheMonth")
          ? "L"
          : values.crons.monthlyDays;

      startTimeCronExpression = `0 ${startTimeMinute} ${startTimeHour} ${monthDay} * ? *`;
      addTimeCronExpression = `0 ${addTimeMinute} ${addTimeHour} ${monthDay} * ? *`;
    }

    cronExpressions.push(startTimeCronExpression);
    if (values.crons.addTime) cronExpressions.push(addTimeCronExpression);
    return cronExpressions;
  };

  const buildEntityFromForm = (everyLegalEntitiesWithoutAll: string[], dealerFilteredOptions: string[]) => {
    const values = form.getValues();
    if (
      values.type?.label ===
      "Capacity of the locations and fleet utilization"
    ) {
      const newAlert: CapacityUtilizationEntityRequest = {
        description: values.description as string,
        type: values.type?.code as string,
        recipientLists: values.recipientLists as string[],
        remainingCapacityDays: values.capacityOfLocations
          .remainingCapacityDays as number,
        remainingPercentage:
          (values?.capacityOfLocations
            ?.remainingPercentage as number),
        remainingCapacity: values.capacityOfLocations
          .remainingCapacity as number,
        usagePercentage:
          (values?.capacityOfLocations?.usagePercentage as number),
        schedulingType: values.schedulingType?.code as string,
        locations: values.locations.map(el => ({
          id: el.code, name: el.label
        })) as SelectElementModelBE[] | [],
        locationGroups: values.locationGroups.map(el => ({
          id: el.code, name: el.label
        })) as SelectElementModelBE[] | [],
        crons: buildCronExpressions(values),
        dealers:dealerFilteredOptions[0],
        legalEntity: everyLegalEntitiesWithoutAll.length === 1 ? everyLegalEntitiesWithoutAll[0] : values.legalEntity?.code as string
      };
      return newAlert;
    } else if (
      values.type?.label ===
      "Availability of vehicles in the locations and fleet utilization"
    ) {
      const newAlert: UtilizationAvailabilityEntityRequest = {
        description: values.description as string,
        type: values.type?.code as string,
        recipientLists: values.recipientLists as string[],
        schedulingType: values.schedulingType?.code as string,
        locations: values.locations.map(el => ({
          id: el.code, name: el.label
        })) as SelectElementModelBE[] | [],
        locationGroups: values.locationGroups.map(el => ({
          id: el.code, name: el.label
        })) as SelectElementModelBE[] | [],
        maxDailyBookings: values.availabilityOfVehicles
          .maxDailyBookings as number,
        securityMargin: values.availabilityOfVehicles
          .securityMargin as number,
        crons: buildCronExpressions(values),
        commercialSegment: values.availabilityOfVehicles.commercialSegment?.code as string,
        dealers: dealerFilteredOptions.length === 1 ? dealerFilteredOptions : [values.dealer?.code as string],
        legalEntities: everyLegalEntitiesWithoutAll.length === 1 ? everyLegalEntitiesWithoutAll : [values.legalEntity?.code as string]
      };
      return newAlert;
    } else if (values.type?.label === "Competitors benchmarks") {
      const newAlert: CompetitorsBenchmarksEntityRequest = {
        description: values.description as string,
        type: values.type?.code as string,
        recipientLists: values.recipientLists as string[],
        topPricePosition: values.competitorsBenchmarks
          .topPricePosition as number,
        bottomPricePosition: values.competitorsBenchmarks
          .bottomPricePosition as number
      };
      return newAlert;
    } else if (
      values.type?.label ===
      "Revenue per day for locations and pick-up date"
    ) {
      const newAlert: RpdEntityRequest = {
        description: values.description as string,
        type: values.type?.code as string,
        recipientLists: values.recipientLists as string[],
        schedulingType: values.schedulingType?.code as string,
        locations: values.locations.map(el => ({
          id: el.code, name: el.label
        })) as SelectElementModelBE[] | [],
        locationGroups: values.locationGroups.map(el => ({
          id: el.code, name: el.label
        })) as SelectElementModelBE[] | [],
        pickupDateFrom: dayjs(values.rpd.pickupDateFrom).format(
          "YYYY-MM-DD"
        ) as string,
        pickupDateTo: dayjs(values.rpd.pickupDateTo).format(
          "YYYY-MM-DD"
        ) as string,
        minRPD: values.rpd.minRPD * 100 as number,
        crons: buildCronExpressions(values),
        commercialSegment: values.rpd.commercialSegment?.code as string,
        dealers: dealerFilteredOptions.length === 1 ? dealerFilteredOptions : [values.dealer?.code as string],
        legalEntities: everyLegalEntitiesWithoutAll.length === 1 ? everyLegalEntitiesWithoutAll : [values.legalEntity?.code as string]
      };
      return newAlert;
    } else if (values.type?.label === "Pricing errors") {
      const newAlert: PricingErrorsEntityRequest = {
        description: values.description as string,
        type: values.type?.code as string,
        recipientLists: values.recipientLists as string[],
        schedulingType: values.schedulingType?.code as string,
        locations: values.locations.map(el => ({
          id: el.code, name: el.label
        })) as SelectElementModelBE[] | [],
        locationGroups: values.locationGroups.map(el => ({
          id: el.code, name: el.label
        })) as SelectElementModelBE[] | [],
        pickupDateFrom: dayjs(
          values.pricingErrors.pickupDateFrom
        ).format("YYYY-MM-DD") as string,
        pickupDateTo: dayjs(values.pricingErrors.pickupDateTo).format(
          "YYYY-MM-DD"
        ) as string,
        unitType: values.pricingErrors.unitType?.code as string,
        minDailyPrice: values.pricingErrors.minDailyPrice * 100 as number,
        crons: buildCronExpressions(values),
        commercialSegment: values.pricingErrors.commercialSegment?.code as string,
        dealers: dealerFilteredOptions.length === 1 ? dealerFilteredOptions : [values.dealer?.code as string],
        legalEntities: everyLegalEntitiesWithoutAll.length === 1 ? everyLegalEntitiesWithoutAll : [values.legalEntity?.code as string]
      };
      return newAlert;
    } else if (values.type?.label === "Advance booking") {
      const newAlert: AdvanceBookingEntityRequest = {
        description: values.description as string,
        type: values.type?.code as string,
        recipientLists: values.recipientLists as string[],
        schedulingType: values.schedulingType?.code as string,
        locations: values.locations.map(el => ({
          id: el.code, name: el.label
        })) as SelectElementModelBE[] | [],
        locationGroups: values.locationGroups.map(el => ({
          id: el.code, name: el.label
        })) as SelectElementModelBE[] | [],
        pickupDateFrom: dayjs(
          values.advanceBooking.pickupDateFrom
        ).format("YYYY-MM-DD") as string,
        pickupDateTo: dayjs(values.advanceBooking.pickupDateTo).format(
          "YYYY-MM-DD"
        ) as string,
        pastDays: values.advanceBooking.pastDays as number,
        pastOrFutureDays: values.advanceBooking
          .pastOrFutureDays as number,
        averageBookingsNumber: values.advanceBooking
          .averageBookingsNumber as number,
        crons: buildCronExpressions(values),
        commercialSegment: values.advanceBooking.commercialSegment?.code as string,
        dealers: dealerFilteredOptions.length === 1 ? dealerFilteredOptions : [values.dealer?.code as string],
        legalEntities: everyLegalEntitiesWithoutAll.length === 1 ? everyLegalEntitiesWithoutAll : [values.legalEntity?.code as string]
      };
      return newAlert;
    } else return null;
  };

  return {
    buildFormFromEntity,
    buildEntityFromForm
  };
};
